<svelte:options customElement="dst-community-reactions" />

<script lang="ts">
	import type { Reactions } from '@/global.d.ts';

	import { communityReactionsExpiry } from '@/config.ts';
	import { Logger } from '@/lib/log.ts';
	import { pushToDataLayer, trackUserAction } from '@/lib/tracking.ts';
	import { showSnackbarMessage } from '@derstandard/utils';
	import { onMount } from 'svelte';
	import { quadIn } from 'svelte/easing';
	import { fly } from 'svelte/transition';

	const log = Logger('header.DstCommunityReactions');

	const edition = window.DERSTANDARD?.pageConfig?.edition || 'at';
	const nodesPath = `${edition}:/${window.DERSTANDARD?.pageConfig?.nodes?.join('/')}`;
	const contentId = window.DERSTANDARD?.pageConfig?.contentId;

	export let articletitle;

	let reactions: Reactions[];
	let reactionCount: number;
	let status: string;
	let userHasVoted = false;
	let loading = false;
	let trackingEvent: string;

	onMount(async () => {
		if (contentId) {
			await getReactionsForArticle(contentId);

			// Check if user already voted - this is only for giving visual feedback to the user, the real check is done in the backend
			if (status === 'active') {
				try {
					const storedReactions = localStorage.getItem('dstCommunityReactions');

					if (storedReactions) {
						const parsedReactions = JSON.parse(storedReactions);
						const updatedStorage = {};

						Object.keys(parsedReactions).forEach((reactionContentId) => {
							if (parsedReactions[reactionContentId].expires > Date.now()) {
								if (reactionContentId === contentId.toString()) {
									userHasVoted = true;
									reactions = reactions.map((reaction) => ({
										...reaction,
										active: reaction.praiseIndex === parsedReactions[reactionContentId].praiseIndex,
									}));
								}
								updatedStorage[reactionContentId] = parsedReactions[reactionContentId];
							}
						});

						if (typeof updatedStorage === 'object' && Object.keys(updatedStorage).length) {
							localStorage.setItem('dstCommunityReactions', JSON.stringify(updatedStorage));
						} else {
							localStorage.removeItem('dstCommunityReactions');
						}
					}
				} catch (e) {
					log.error('getting reactions from localstorage failed ', e);
				}
			}
		}
	});

	/**
	 * Initial fetch of all community reactions. Also gets the overall
	 * count and the current status ("active", "inactive" or "uninitialized")
	 * @param contentId contentId of the current article
	 * @returns
	 */
	async function getReactionsForArticle(contentId: number): Promise<void> {
		let response;

		loading = true;

		try {
			response = await window.fetch(
				`${import.meta.env.VITE_BASE_URL_COMMUNITY_REACTIONS_GET}?contentId=${contentId}&nodePath=${encodeURIComponent(nodesPath)}`,
				{
					headers: {
						Accept: 'application/json',
						Authorization: null,
						'Content-Type': 'application/json',
					},
					method: 'GET',
				},
			);

			const reactionResponse = await response.json();

			reactions = reactionResponse?.reactions;
			reactionCount = reactionResponse?.count;
			status = reactionResponse?.status;
			loading = false;
		} catch (e) {
			loading = false;
			log.error('Failed to load Community Reactions, ', e);
		}
	}

	/**
	 * Saves reaction for the current article
	 * @param reactionIndex praiseIndex of reaction
	 * @param undoVote deletes previous vote if true
	 * @returns
	 */
	async function handleReactionClick(reactionIndex: number, undoVote: boolean): Promise<void> {
		log.debug('reaction button clicked');
		let response;

		loading = true;

		try {
			response = await window.fetch(`${import.meta.env.VITE_BASE_URL_COMMUNITY_REACTIONS}`, {
				body: JSON.stringify({
					contentId: contentId,
					nodePath: nodesPath,
					praiseIndex: reactionIndex,
				}),
				headers: {
					Accept: 'application/json',
					Authorization: null,
					'Content-Type': 'application/json',
				},
				method: undoVote ? 'DELETE' : 'POST',
			});

			const reactionResponse = !undoVote ? await response?.json() : '';

			if (response?.ok && (undoVote || reactionResponse?.status === 'success')) {
				// update reactions
				log.debug('update reactions');
				reactions = reactions.map((reaction) => {
					let reactionCount = reaction.count;

					if (reaction.active && reaction.praiseIndex !== reactionIndex) {
						reactionCount--;
					} else if (!reaction.active && reaction.praiseIndex === reactionIndex) {
						reactionCount++;
					} else if (reaction.active && reaction.praiseIndex === reactionIndex) {
						reactionCount--;
					}

					return {
						...reaction,
						active: undoVote ? false : reaction.praiseIndex === reactionIndex,
						count: reactionCount,
					};
				});

				if (undoVote) {
					trackingEvent = 'comreact_removed';
				} else if (userHasVoted) {
					trackingEvent = 'comreact_changed';
				} else {
					trackingEvent = 'comreact_saved';
				}

				// increase overall count once
				if (!userHasVoted) {
					log.debug('new reaction saved');
					reactionCount++;
					userHasVoted = true;
				} else if (undoVote) {
					log.debug('previous reaction reverted');
					reactionCount--;
					userHasVoted = false;
				}

				pushToDataLayer({
					comReactionContentId: contentId,
					event: 'tracking.userAction',
					userActionType: trackingEvent,
				});

				// track user clicks
				pushToDataLayer({
					comReactionContentId: contentId,
					event: 'tracking.userAction',
					userActionType: 'comreact_react',
				});

				sendDataToWebCube(reactionIndex);

				try {
					log.debug('save reactions to localStorage');

					const storedReactions = JSON.parse(localStorage.getItem('dstCommunityReactions')) || {};
					const expiredDate = new Date();

					expiredDate.setDate(expiredDate.getTime() + communityReactionsExpiry); // expires in 3 hours (although user vote counts as new after 10 minutes)

					if (undoVote) {
						storedReactions[contentId] = undefined;
					} else {
						storedReactions[contentId] = {
							expires: storedReactions[contentId]?.expires || expiredDate.getTime(),
							praiseIndex: reactionIndex,
						};
					}

					localStorage.setItem('dstCommunityReactions', JSON.stringify(storedReactions));
				} catch (e) {
					log.error('saveing reactions to localStorage failed ', e);
				}
			} else {
				showSnackbarMessage(
					'Es ist ein Fehler aufgetreten. Versuchen Sie es bitte zu einem späteren Zeitpunkt erneut.',
				);
			}

			loading = false;
		} catch (e) {
			showSnackbarMessage('Es ist ein Fehler aufgetreten. Versuchen Sie es bitte zu einem späteren Zeitpunkt erneut.');
			loading = false;
			log.error('Failed to load Community Reactions, ', e);
		}
	}

	/**
	 * Map the various reaction types to specific actions and send them to BI
	 * for analysis.
	 *
	 * Note: this function is subject to change if there are new or different
	 * reactions added.
	 * @param praiseIndex the reactions index
	 */
	function sendDataToWebCube(praiseIndex: number): void {
		switch (praiseIndex) {
			case 0:
				trackUserAction('comreact_like');
				break;

			case 1:
				trackUserAction('comreact_info');
				break;

			case 2:
				trackUserAction('ccomreact_helpful');
				break;

			case 3:
				trackUserAction('comreact_touching');
				break;

			case 4:
				trackUserAction('comreact_entertaining');
				break;

			default:
				trackUserAction('comreact_react');
				break;
		}
	}

	/**
	 * Animation function - lets the "like" heart pulsate (scale)
	 * @param node
	 * @param param1 animation parameters
	 * @param param1.duration
	 * @returns Animation information
	 */
	function pulse(node: Node, { duration }): Record<string, unknown> {
		return {
			css: (t) => `transform: scale(${1 + 0.2 * quadIn(t)});`,
			duration,
		};
	}
</script>

{#if reactions?.length}
	<aside class="reactions">
		<h3 class="reactions--headline">
			Wie finden Sie den Artikel?
			<span class="sr-only">{articletitle}</span>
			{#if reactionCount}
				<small><span>{reactionCount}</span>{` Reaktion${reactionCount > 1 ? 'en' : ''}`}</small>
			{/if}
		</h3>
		<div class="reactions--buttons">
			{#each reactions as { active, count, label, praiseIndex }}
				<button
					type="button"
					class:active={active}
					class:was-active={!active}
					on:click={() => (!loading ? handleReactionClick(praiseIndex, active) : null)}
					disabled={status === 'inactive'}
					aria-live="polite"
				>
					{#if count > 0}
						{#key count}
							<strong in:fly={{ duration: 250, opacity: 1, y: -50 }} out:fly={{ duration: 250, opacity: 1, y: 30 }}
								>{count > 0 ? count : ''}</strong
							>
						{/key}
					{/if}
					{#if label === ''}
						<span class="sr-only">gefällt mir</span>
						{#key count}
							<svg in:pulse={{ duration: active ? 250 : 0 }} viewBox="0 0 24 24">
								<path
									fill="transparent"
									d="M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5C2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z"
								/>
								<path
									fill="currentColor"
									d="M12.1,18.55L12,18.65L11.89,18.55C7.14,14.24 4,11.39 4,8.5C4,6.5 5.5,5 7.5,5C9.04,5 10.54,6 11.07,7.36H12.93C13.46,6 14.96,5 16.5,5C18.5,5 20,6.5 20,8.5C20,11.39 16.86,14.24 12.1,18.55M16.5,3C14.76,3 13.09,3.81 12,5.08C10.91,3.81 9.24,3 7.5,3C4.42,3 2,5.41 2,8.5C2,12.27 5.4,15.36 10.55,20.03L12,21.35L13.45,20.03C18.6,15.36 22,12.27 22,8.5C22,5.41 19.58,3 16.5,3Z"
								/>
							</svg>
						{/key}
					{:else}
						{label}
					{/if}
				</button>
			{/each}
		</div>
		{#if status === 'inactive'}
			<p class="inactive-hint">Zu diesem Inhalt können keine Reaktionen mehr gespeichert werden.</p>
		{/if}
	</aside>
{/if}

<style lang="scss" global>
	@use './DstCommunityReactions.scss';
</style>
